import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Input, Button, Select, Checkbox } from 'antd';
import {
  createAgentAction,
  listAgentsAction,
  listRetellAgentsAction,
} from '../../../../store/actions/agents.action';
import { showToastError } from 'src/utilities/errortoast';
import OrganizationFilter from '../OrganizationFilter';

const AddNewAgent = ({ isOpen, setIsOpen, orgUuid }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [isCloning, setIsCloning] = useState(false);
  const retellAgents = useSelector(
    (state) => state.agentsState.retellAgentsList
  );

  useEffect(() => {
    dispatch(listRetellAgentsAction());
  }, [dispatch]);

  const handleClose = () => {
    form.resetFields();
    setIsCloning(false);
    setIsOpen(false);
  };

  const handleSubmit = (values) => {
    if (!orgUuid) {
      showToastError('Organization UUID is required');
      return;
    }

    const payload = {
      name: values.name.trim(),
    };

    if (isCloning && values.mirror_agent_id) {
      payload.mirror_agent_id = values.mirror_agent_id;
    } else {
      payload.external_id = values.external_id.trim();
    }

    dispatch(
      createAgentAction(orgUuid, payload, () => {
        handleClose();
        dispatch(listAgentsAction(orgUuid));
      })
    );
  };

  const handleCloneChange = (e) => {
    const checked = e.target.checked;
    setIsCloning(checked);
    if (checked) {
      form.setFieldsValue({ external_id: undefined });
    } else {
      form.setFieldsValue({ mirror_agent_id: undefined, name: undefined });
    }
  };

  const handleAgentSelect = (agentId) => {
    const selectedAgent = retellAgents.find(
      (agent) => agent.agent_id === agentId
    );
    if (selectedAgent) {
      form.setFieldsValue({ name: `${selectedAgent.agent_name} (copy)` });
    }
  };

  return (
    <Modal
      title="Add New Agent"
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {/* Organization Filter Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
            Organization Scope
          </h3>
          <Form.Item label="Organization">
            <OrganizationFilter disabled={false} />
          </Form.Item>
        </div>

        {/* Agent Details Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">
            Agent Details
          </h3>

          <Form.Item name="clone_mode" valuePropName="checked">
            <Checkbox onChange={handleCloneChange}>
              Clone existing agent (from Retell)
            </Checkbox>
          </Form.Item>

          {isCloning && (
            <Form.Item
              name="mirror_agent_id"
              label="Select Retell Agent"
              rules={[
                {
                  required: true,
                  message: 'Please select a Retell agent to clone',
                },
              ]}
            >
              <Select
                placeholder="Select a Retell agent"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={handleAgentSelect}
                options={retellAgents.map((agent) => ({
                  label: `${agent.agent_name}${
                    agent.version ? ` (v${agent.version})` : ''
                  }`,
                  value: agent.agent_id,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            name="name"
            label="Agent Name"
            rules={[
              { required: true, message: 'Please enter agent name' },
              { min: 2, message: 'Agent name must be at least 2 characters' },
              {
                max: 100,
                message: 'Agent name must not exceed 100 characters',
              },
            ]}
          >
            <Input placeholder="Enter agent name" maxLength={100} />
          </Form.Item>

          {!isCloning && (
            <Form.Item
              name="external_id"
              label="External ID"
              rules={[
                { required: true, message: 'Please enter external ID' },
                { min: 1, message: 'External ID must be at least 1 character' },
                {
                  max: 500,
                  message: 'External ID must not exceed 500 characters',
                },
              ]}
            >
              <Input placeholder="Enter external ID" maxLength={500} />
            </Form.Item>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={handleClose} className="mr-2">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" disabled={!orgUuid}>
            Create Agent
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddNewAgent;
